import React, { useMemo } from 'react';

const HEX_SIZE = 46;

const TEAM_COLORS = {
  orange: '#FF8C00',
  green:  '#2E8B57',
  blue:   '#1E88E5',
  purple: '#8A2BE2',
  white:  '#FAFAF8',
};

const TEAM_STROKE = {
  orange: '#CC6600',
  green:  '#1B6B40',
  blue:   '#145A9E',
  purple: '#5D1D96',
  white:  '#aab0c0',
};

function hexToPixel(q, r, size) {
  // odd-r offset coordinates
  const x = size * Math.sqrt(3) * (q + (r % 2) * 0.5);
  const y = size * (3 / 2 * r);
  return { x, y };
}

function hexCorners(cx, cy, size) {
  const points = [];
  for (let i = 0; i < 6; i++) {
    const angleDeg = 60 * i - 30; // pointy top
    const angleRad = (Math.PI / 180) * angleDeg;
    points.push({
      x: cx + size * Math.cos(angleRad),
      y: cy + size * Math.sin(angleRad),
    });
  }
  return points;
}

function pointsToSVG(pts) {
  return pts.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
}

export default function HexGridRect({ grid, mode, onCellClick, winPath, gridRadius = 5 }) {
  const coords = useMemo(() => {
    if (!grid) return [];
    return Object.values(grid).map(c => ({ q: c.q, r: c.r }));
  }, [grid]);

  const pixels = useMemo(() => coords.map(({ q, r }) => hexToPixel(q, r, HEX_SIZE)), [coords]);
  
  if (!grid || pixels.length === 0) {
    return (
      <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>
        جاري تحميل الشبكة 2P...
      </div>
    );
  }

  const minX = Math.min(...pixels.map(p => p.x)) - HEX_SIZE * 1.5;
  const maxX = Math.max(...pixels.map(p => p.x)) + HEX_SIZE * 1.5;
  const minY = Math.min(...pixels.map(p => p.y)) - HEX_SIZE * 1.5;
  const maxY = Math.max(...pixels.map(p => p.y)) + HEX_SIZE * 1.5;
  const W = maxX - minX;
  const H = maxY - minY;

  const PAD = 30;
  const totalW = W + PAD * 2;
  const totalH = H + PAD * 2;
  const ox = -minX + PAD;
  const oy = -minY + PAD;

  return (
    <svg
      width={totalW}
      height={totalH}
      viewBox={`0 0 ${totalW} ${totalH}`}
      style={{ display: 'block', maxWidth: '100%', borderRadius: 12, overflow: 'hidden' }}
    >
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="hex-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="rgba(0,0,0,0.5)" />
        </filter>
      </defs>

      {/* ── Background Borders matching User Image ── */}
      {mode === '4p' ? (
        <g>
          <polygon points={`0,0 ${totalW},0 ${totalW/2},${totalH/2}`} fill={TEAM_COLORS.orange} />
          <polygon points={`0,0 0,${totalH} ${totalW/2},${totalH/2}`} fill={TEAM_COLORS.green} />
          <polygon points={`0,${totalH} ${totalW},${totalH} ${totalW/2},${totalH/2}`} fill={TEAM_COLORS.purple} />
          <polygon points={`${totalW},0 ${totalW},${totalH} ${totalW/2},${totalH/2}`} fill={TEAM_COLORS.blue} />
        </g>
      ) : (
        <g>
          <rect x={0} y={0} width={totalW} height={totalH} fill={TEAM_COLORS.green} />
          <rect x={0} y={0} width={totalW} height={HEX_SIZE * 1.8} fill={TEAM_COLORS.orange} />
          <rect x={0} y={totalH - HEX_SIZE * 1.8} width={totalW} height={HEX_SIZE * 1.8} fill={TEAM_COLORS.orange} />
        </g>
      )}

      {/* ── Hexagon cells ── */}
      {coords.map(({ q, r }) => {
        const key = `${q},${r}`;
        const cell = grid[key];
        if (!cell) return null;

        const { x, y } = hexToPixel(q, r, HEX_SIZE);
        const cx = x + ox;
        const cy = y + oy;
        const corners = hexCorners(cx, cy, HEX_SIZE - 1.5); // Slightly tighter borders
        const isWin = winPath && winPath.includes(key);
        const color = cell.color || 'white';

        let fillColor = color === 'white' ? '#1e2235' : TEAM_COLORS[color];
        if (color === 'focus') fillColor = ''; // Managed via CSS important rule

        const strokeColor = isWin ? '#fff' : (color === 'white' ? '#3a4060' : TEAM_STROKE[color]);
        const strokeW = isWin ? 3 : 1.5;

        return (
          <g
            key={key}
            onClick={(e) => onCellClick(e, key, cell)}
            style={{ cursor: 'pointer' }}
            filter={isWin ? 'url(#glow)' : 'url(#hex-shadow)'}
          >
            <polygon
              className={color === 'focus' ? 'hex-focus' : ''}
              points={pointsToSVG(corners)}
              fill={color === 'focus' ? undefined : fillColor}
              stroke={color === 'focus' ? undefined : strokeColor}
              strokeWidth={color === 'focus' ? undefined : strokeW}
              style={{ transition: color === 'focus' ? 'none' : 'fill 0.3s ease, stroke 0.2s ease' }}
            />
            {/* Letter text */}
            <text
              x={cx}
              y={cy + 1}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={HEX_SIZE * 0.52}
              fontWeight="700"
              fontFamily="Tajawal, sans-serif"
              fill={color === 'white' ? '#e8eaf0' : 'rgba(255,255,255,0.92)'}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {cell.letter}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
